// requestSlice.ts
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { reset } from "./reset";
import { getAllApprovalRequests } from "@grudder/apiCalls";
import { AUTH_POST } from "@grudder/app/actions-client";
import { config } from "@grudder/lib/config";

interface Request {
  _id: string;
  lead?: { _id: string };
  createdAt: string;
}

interface RequestState {
  request: Request[];
  total: number;
  loading: boolean;
  loadingMore: boolean;
  creating?: boolean;
  currentLeadId?: string;
  statusFilter?: string;
  typeFilter?: string;
}

const initialState: RequestState = {
  request: [],
  total: 0,
  loading: false,
  loadingMore: false,
  creating: false,
  currentLeadId: undefined,
  statusFilter: "all",
  typeFilter: "all",
};

// Define an async thunk to fetch requests for a lead (with optional filters)
export const fetchRequestsForLead = createAsyncThunk<
  { data: Request[]; total: number },
  { leadId: string; status?: string; requestType?: string }
>(
  "request/fetchRequestsForLead",
  async ({ leadId, status = "all", requestType = "all" }, { getState, dispatch }) => {
  const state = getState() as { request: RequestState };
  const { request, total, currentLeadId, statusFilter, typeFilter } = state.request ?? {} as RequestState;

  const filtersMatch =
    currentLeadId === leadId &&
    (statusFilter ?? "all") === (status ?? "all") &&
    (typeFilter ?? "all") === (requestType ?? "all");

  const hasDataForFilters = (request?.length ?? 0) > 0;

    if (!filtersMatch || !hasDataForFilters) {
      dispatch(startRequestLoading());
      dispatch(setRequestFilters({ leadId, status, requestType }));
      dispatch(initializeRequests({ request: [], total: 0 }));
      const response = await getAllApprovalRequests({
        limit: 10,
        skip: 0,
        lead: leadId,
        status: status === "all" ? undefined : status,
        type: requestType === "all" ? undefined : requestType,
      });
      return response;
    }

  const returnData = { data: request, total };
  return returnData;
  }
);

// Define an async thunk to load more requests for a lead with filters
export const loadMoreRequestsForLead = createAsyncThunk<
  { data: Request[]; total: number },
  { leadId: string; limit: number; skip: number; status?: string; requestType?: string }
>(
  "request/loadMoreRequestsForLead",
  async ({ leadId, limit, skip, status = "all", requestType = "all" }) => {
    const response = await getAllApprovalRequests({
      limit,
      skip,
      lead: leadId,
      status: status === "all" ? undefined : status,
      type: requestType === "all" ? undefined : requestType,
    });
    return response;
  }
);

// Thunk to create a new approval request
export const createApprovalRequest = createAsyncThunk<any, { leadId: string; formData: { type: string; requestedGroup: string; requestedUser?: string; requestNote?: string } }>(
  "request/createApprovalRequest",
  async ({ leadId, formData }, { rejectWithValue }) => {
    try {
      const payload = { lead: leadId, status: "REQUESTED", ...formData };
      const res = await AUTH_POST(`${config.baseURL}/api/approval-request`, payload);
      return res;
    } catch (err: any) {
      return rejectWithValue(err);
    }
  }
);

const requestSlice = createSlice({
  name: "request",
  initialState,
  reducers: {
    initializeRequests(
      state,
      action: PayloadAction<{ request: Request[]; total: number }>
    ) {
      state.request = [...action.payload.request].sort(
        (requestA, requestB) =>
          new Date(requestB.createdAt).getTime() -
          new Date(requestA.createdAt).getTime()
      );
      state.total = action.payload.total;
    },
    startRequestLoading(state) {
      state.loading = true;
    },
    setRequestFilters(
      state,
      action: PayloadAction<{ leadId?: string; status?: string; requestType?: string }>
    ) {
      state.currentLeadId = action.payload.leadId ?? state.currentLeadId;
      state.statusFilter = action.payload.status ?? state.statusFilter ?? "all";
      state.typeFilter = action.payload.requestType ?? state.typeFilter ?? "all";
    },
    addNewRequest(state, action: PayloadAction<Request>) {
      const requestIndex = state.request.findIndex(
        (request) => request._id === action.payload._id
      );
      const updatedRequestsMap = new Map(
        state.request.map((request) => [request._id, request])
      );
      updatedRequestsMap.set(action.payload._id, action.payload);
      state.request = Array.from(updatedRequestsMap.values()).sort(
        (requestA, requestB) =>
          new Date(requestB.createdAt).getTime() -
          new Date(requestA.createdAt).getTime()
      );
      if (requestIndex === -1) {
        state.total += 1;
      }
    },
    handleAddRequests(
      state,
      action: PayloadAction<{ request: Request[]; total: number }>
    ) {
      const updatedRequestsMap = new Map(
        state.request.map((request) => [request._id, request])
      );
      action.payload.request.forEach((newRequest) => {
        updatedRequestsMap.set(newRequest._id, newRequest);
      });
      state.request = Array.from(updatedRequestsMap.values()).sort(
        (requestA, requestB) =>
          new Date(requestB.createdAt).getTime() -
          new Date(requestA.createdAt).getTime()
      );
      state.total = action.payload.total;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRequestsForLead.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchRequestsForLead.fulfilled, (state, action) => {
        state.loading = false;
        state.request = [...action.payload.data].sort(
          (requestA, requestB) =>
            new Date(requestB.createdAt).getTime() -
            new Date(requestA.createdAt).getTime()
        );
        state.total = action.payload.total;
      })
      .addCase(fetchRequestsForLead.rejected, (state) => {
        state.loading = false;
        state.request = [];
        state.total = 0;
      })
      .addCase(loadMoreRequestsForLead.pending, (state) => {
        state.loadingMore = true;
      })
      .addCase(loadMoreRequestsForLead.fulfilled, (state, action: any) => {
        state.loadingMore = false;
        const updatedRequestsMap = new Map(
          state.request.map((request) => [request._id, request])
        );
        action.payload.data.forEach((newRequest: Request) => {
          updatedRequestsMap.set(newRequest._id, newRequest);
        });
        state.request = Array.from(updatedRequestsMap.values()).sort(
          (requestA, requestB) =>
            new Date(requestB.createdAt).getTime() -
            new Date(requestA.createdAt).getTime()
        );
        state.total = action.payload.total;
      })
      .addCase(loadMoreRequestsForLead.rejected, (state) => {
        state.loadingMore = false;
        state.request = [];
        state.total = 0;
      })
      // create approval request cases
      .addCase(createApprovalRequest.pending, (state) => {
        state.creating = true;
      })
      .addCase(createApprovalRequest.fulfilled, (state, action) => {
        state.creating = false;
        const res: any = action.payload;
        // Determine if it matches current filters so we show it & increment total
        const matchesLead = res?.lead?._id === state.currentLeadId || res?.lead === state.currentLeadId;
        const matchesStatus = !state.statusFilter || state.statusFilter === "all" || state.statusFilter === res?.status;
        const matchesType = !state.typeFilter || state.typeFilter === "all" || state.typeFilter === res?.type;
        if (matchesLead && matchesStatus && matchesType) {
          const exists = state.request.find((r) => r._id === res._id);
            // Insert at correct position using map logic similar to addNewRequest
            const updatedMap = new Map(state.request.map((r) => [r._id, r]));
            updatedMap.set(res._id, res);
            state.request = Array.from(updatedMap.values()).sort(
              (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
            if (!exists) state.total += 1;
        }
      })
      .addCase(createApprovalRequest.rejected, (state) => {
        state.creating = false;
      })
      .addCase(reset, () => initialState); // Handle reset action
  },
});

export const {
  initializeRequests,
  addNewRequest,
  handleAddRequests,
  startRequestLoading,
  setRequestFilters,
} = requestSlice.actions;
export default requestSlice.reducer;
